import {ReactElement} from "react";
import styled from "styled-components";

import {downloadFichier} from "@/Api/fichier";
import IconArrowBottom from "@/assets/iconArrowBot.svg?react";
import IconArrowRight from "@/assets/iconArrowRight.svg?react";
import IconCross from "@/assets/iconCross.svg?react";
import IconDownload from "@/assets/iconDownload.svg?react";
import IconValidate from "@/assets/iconValide.svg?react";
import {AppContext} from "@/context/AppContext";
import {calculePourcentage} from "@/fonction/calcule";
import {breakpoints, quicksandMedium, quicksandSemiBold} from "@/GlobalStyle";
import {useGetContext} from "@/hooks/useGetContext/useGetContext";
import {useLoadingState} from "@/hooks/useLoadingState/useLoadingState";
import {AppContext as AppContextType} from "@/ts/appContext";
import {KpiEnCours} from "@/ts/kpi";

import {Button} from "../Button/Button";
import {ButtonForFetch} from "../Button/ButtonForFetch";

const ContentModalConsulterKpi = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  & button {
    justify-content: center;
  }
`;
const HeaderModalConsulterKpi = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  row-gap: 20px;
  column-gap: 30px;
  flex: 1;
  text-align: center;
  @media (max-width: ${breakpoints.md}) {
    flex-direction: column;
  }
`;
const TitleKpiPrintValeur = styled.h4<{$isOldValue: boolean}>`
  font-size: 1.25rem;
  margin: 5px;
  color: ${({$isOldValue, theme}) =>
    $isOldValue ? theme.colors.lightPrimary : theme.colors.darkPrimary};
`;
const ContentModalConsulterKpiPrintValeur = styled.div<{$isOldValue: boolean}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  background-color: ${({$isOldValue, theme}) =>
    $isOldValue ? theme.colors.lightPrimary : theme.colors.darkPrimary};
  padding: 20px;
  & p {
    margin: 0;
    color: white;
    display: flex;
    align-items: center;
    flex: 1;
    ${quicksandSemiBold}
  }
  & div {
    display: flex;
    flex-direction: row;
    gap: 10px;
    width: 100%;
  }
  span {
    color: ${({$isOldValue, theme}) =>
      $isOldValue ? theme.colors.lightPrimary : theme.colors.darkPrimary};

    background-color: white;
    padding: 5px;
    border-radius: 5px;
    min-width: 75px;
  }
`;
const IconArrowRightStyled = styled(IconArrowRight)`
  fill: ${({theme}) => theme.colors.mediumPrimary};
  width: 40px;
  height: 40px;
  margin-top: 25px;
  @media (max-width: ${breakpoints.md}) {
    display: none;
  }
`;
const IconArrowBottomStyled = styled(IconArrowBottom)`
  fill: ${({theme}) => theme.colors.mediumPrimary};
  width: 40px;
  height: 40px;
  display: none;
  @media (max-width: ${breakpoints.md}) {
    display: block;
  }
`;

const CommentaireContent = styled.div`
  position: relative;
  border: solid 2px ${({theme}) => theme.colors.darkPrimary};
  background-color: white;
  & label {
    position: absolute;
    top: -17px;
    left: 15px;
    background-color: white;
    padding: 5px 5px 0 5px;
    ${quicksandSemiBold}
    color: ${({theme}) => theme.colors.darkPrimary};
  }
  & p {
    font-size: 0.875rem;
    padding: 10px;
    ${quicksandMedium}
  }
`;

const NoFileContent = styled.p`
  text-align: center;
  ${quicksandMedium}
  color: ${({theme}) => theme.colors.alert};
  margin: 10px;
`;

const TitleModal = styled.h3`
  text-align: center;
  ${quicksandSemiBold}
  font-size: 1.25rem;
  color: ${({theme}) => theme.colors.darkPrimary};
  margin: 20px 0 0 0;
`;

export const ValidOrRefuseButtonContent = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
`;

export function ContentModalRecapUpdateKpi({
  kpi,
  handleAction,
  closeModal,
}: {
  kpi: KpiEnCours;
  handleAction: (action: "accepter" | "refuser") => void;
  closeModal: () => void;
}): ReactElement {
  const {API} = useGetContext<AppContextType>(AppContext, "AppContext");
  const fichierPuriel = kpi?.evenements?.[0]?.fichiers?.length > 1 ? "s" : "";
  const loadingState = useLoadingState();
  return (
    <ContentModalConsulterKpi>
      <TitleModal>{`Demande d'évolution du KPI : ${kpi.nom}`}</TitleModal>
      <HeaderModalConsulterKpi>
        <div>
          <TitleKpiPrintValeur $isOldValue={true}>
            {"Actuellement"}
          </TitleKpiPrintValeur>
          <ContentModalConsulterKpiPrintValeur $isOldValue={true}>
            <div>
              <p>{"Valeur "}</p>
              <span>{kpi?.valeur}</span>
            </div>
            <div>
              <p>{"Objectif "}</p>
              <span>{kpi?.objectif}</span>
            </div>
            <div>
              <p>{"Avancement "}</p>
              <span>{`${calculePourcentage(kpi?.valeur, kpi?.objectif)} %`}</span>
            </div>
          </ContentModalConsulterKpiPrintValeur>
        </div>
        <IconArrowRightStyled />
        <IconArrowBottomStyled />
        <div>
          <TitleKpiPrintValeur $isOldValue={false}>
            {"Proposé"}
          </TitleKpiPrintValeur>
          <ContentModalConsulterKpiPrintValeur $isOldValue={false}>
            <div>
              <p>{"Valeur "}</p>
              <span>{kpi?.valeur + kpi?.evenements?.[0]?.increment}</span>
            </div>
            <div>
              <p>{"Objectif "}</p>
              <span>{kpi?.objectif}</span>
            </div>
            <div>
              <p>{"Avancement "}</p>
              <span>{`${calculePourcentage(kpi?.valeur + kpi?.evenements?.[0]?.increment, kpi?.objectif)} %`}</span>
            </div>
          </ContentModalConsulterKpiPrintValeur>
        </div>
      </HeaderModalConsulterKpi>

      {kpi?.evenements?.[0]?.commentaire ? (
        <CommentaireContent>
          <label>Commentaire</label>
          <p>{kpi?.evenements?.[0]?.commentaire}</p>
        </CommentaireContent>
      ) : null}
      {kpi?.evenements?.[0]?.fichiers?.length > 0 ? (
        <ButtonForFetch
          loadingState={loadingState}
          messageSuccess={`Fichier${fichierPuriel} téléchargé${fichierPuriel} avec succès`}
          messageError="Erreur lors du téléchargement"
          labelLoading="Téléchargement en cours..."
          labelError="Erreur lors du téléchargement"
          labelSuccess={`Fichier${fichierPuriel} téléchargé${fichierPuriel} avec succès`}
          label={`Télécharger le${fichierPuriel} fichier${fichierPuriel}`}
          icon={<IconDownload />}
          onClick={async () => {
            loadingState.executeWithLoading(async () => {
              const allFichierIds = kpi?.evenements?.[0]?.fichiers?.map(
                fichier => fichier.fichier_id
              );
              await downloadFichier(
                API,
                allFichierIds,
                undefined,
                `Pièce-jointe-KPI-${kpi.nom}.zip`
              );
            });
          }}
        />
      ) : (
        <NoFileContent>Aucun fichier n'a été joint</NoFileContent>
      )}
      <ValidOrRefuseButtonContent>
        <Button
          data-testid="kpi-button-refuser"
          icon={IconCross}
          label="Refuser"
          variant="negate"
          onClick={() => {
            try {
              handleAction("refuser");
              closeModal();
            } catch (error) {
              console.error(error);
            }
          }}
        />
        <Button
          data-testid="kpi-button-accepter"
          label="Accepter"
          icon={IconValidate}
          variant="validate"
          onClick={() => {
            try {
              handleAction("accepter");
              closeModal();
            } catch (error) {
              console.error(error);
            }
          }}
        />
      </ValidOrRefuseButtonContent>
    </ContentModalConsulterKpi>
  );
}
